import { Link } from 'react-router-dom';

import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { formatDuration, type JobView } from 'src/lib/jobBoard';
import { JOB_STATUS_LABEL, JOB_STATUS_TONE, STAGE_LABEL } from 'src/lib/pipelineStages';
import { formatDateTime } from 'src/lib/time';
import type { PipelineStage } from 'src/types/editorial';

/**
 * What has been asked of the pipeline and what it is doing about it.
 *
 * A three-hour broadcast produces nothing visible for hours, so the stage it is on is the whole
 * feedback loop — without it a running job and a dead box look identical, and the editor submits
 * the video again. The stage track is that loop drawn rather than described: six steps, each with
 * its own standing, so "where is it" is answered without reading a sentence.
 */
export function JobQueue({
  views,
  emptyNote,
  onRetry,
  retrying,
}: {
  views: JobView[];
  emptyNote: string;
  /** Absent on the history of another screen, where resubmitting is not this list's job. */
  onRetry?: (view: JobView) => void;
  retrying?: string;
}) {
  if (views.length === 0) return <p className="jobs__empty">{emptyNote}</p>;

  return (
    <ul className="jobs">
      {views.map(view => (
        <li key={view.job.jobId}>
          <JobRow view={view} onRetry={onRetry} retrying={retrying} />
        </li>
      ))}
    </ul>
  );
}

function JobRow({
  view,
  onRetry,
  retrying,
}: {
  view: JobView;
  onRetry?: (view: JobView) => void;
  retrying?: string;
}) {
  const { job, progress } = view;
  const target = job.resolvedStreamId || job.streamId;
  const lastStage = job.stages[job.stages.length - 1];

  return (
    <article className="job" data-status={job.status}>
      <header className="job__head">
        <Badge tone={JOB_STATUS_TONE[job.status]}>{JOB_STATUS_LABEL[job.status]}</Badge>
        <span className="job__target">
          {target ? (
            // Linkable as soon as the run has a stream, which is well before it finishes.
            <Link to={`/stream/${encodeURIComponent(target)}`}>{target}</Link>
          ) : (
            job.sourceUrl
          )}
        </span>
        <span className="job__timing">
          {job.status === 'pending' ? (
            <>en cola {formatDuration(view.waitingMs)}</>
          ) : job.status === 'running' ? (
            <>corriendo {formatDuration(view.runningMs)}</>
          ) : (
            <>
              {formatDuration(view.runningMs)} · {formatDateTime(job.finishedAt ?? job.createdAt)}
            </>
          )}
        </span>
      </header>

      {/* Six steps, in dependency order, each carrying what happened to it. Never animated: the
          progress is state, not a moving graphic (Art Direction §11). */}
      <ol className="job__track" aria-label={`Avance: ${progress.done} de ${progress.total} etapas`}>
        {progress.track.map(({ stage, standing }) => (
          <li key={stage} className="job__step" data-standing={standing}>
            <span className="job__step-bar" aria-hidden="true" />
            <span className="job__step-name">{STAGE_LABEL[stage]}</span>
          </li>
        ))}
      </ol>

      <p className="job__stages">
        {progress.done} de {progress.total} etapas · hasta{' '}
        <strong>{lastStage ? STAGE_LABEL[lastStage as PipelineStage] : '—'}</strong>
        {job.status === 'running' && progress.current && (
          <>
            {' · '}
            <span className="job__current">procesando {STAGE_LABEL[progress.current]}</span>
          </>
        )}
        {job.status === 'pending' && ' · esperando a que la máquina despierte'}
        {job.requestedBy && ` · pidió ${job.requestedBy}`}
      </p>

      {job.error && (
        <div className="job__failure">
          <p className="job__error" title={job.error}>
            {job.error}
          </p>
          {onRetry &&
            (view.retryable ? (
              <Button
                variant="secondary"
                busy={retrying === job.jobId}
                onClick={() => onRetry(view)}
              >
                Pedirlo de nuevo
              </Button>
            ) : (
              // Offering a retry on a refusal about the request itself teaches an editor that the
              // button does nothing, which costs more than the missing button.
              <p className="job__no-retry">
                Reintentar no ayudaría: la corrida no falló por la máquina, sino por lo que se le pidió.
              </p>
            ))}
        </div>
      )}
    </article>
  );
}
